import { useDisabledPromoCodesList } from '@if/core'
import { parseISO } from '@if/core/date'
import { EPaymentPlan, PlanPromoRule } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { ControlledSelect, ControlledStringDateOnlyPicker, ControlledTextField } from '@if/mui'
import { Grid, MenuItem } from '@mui/material'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { isVariableInterest } from '@src/data/worksheet-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { SelectValueListItem } from '@src/types/SelectValueListItem'
import { EditPersonalLoanWorksheetDto } from '@src/types/WorksheetSchema'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
interface Props {
  creditApplication: CreditApplication
  merchantPayments: { label: string; value: string; id: string }[]
  disabledAmountInput?: boolean
  maxTermDuration?: number
  loanTermChoicesList: SelectValueListItem[]
  isLoadingTerm: boolean
  planPromoRules?: PlanPromoRule[]
  firstPaymentDateOptions: Date[]
}

const WorksheetDetails = ({
  creditApplication,
  merchantPayments,
  disabledAmountInput,
  loanTermChoicesList,
  isLoadingTerm,
  planPromoRules,
  firstPaymentDateOptions,
}: Props) => {
  const { t } = useTranslation()
  const { control } = useFormContext<EditPersonalLoanWorksheetDto>()
  const [paymentPlanId] = useWatch({ control, name: ['paymentPlanId'] })
  const firstPaymentDateOptionItems = useMemo(
    () =>
      firstPaymentDateOptions?.map((item) => {
        const formattedDate = formatDate(item)
        return (
          <MenuItem key={formattedDate} value={formattedDate}>
            {formattedDate}
          </MenuItem>
        )
      }),
    [firstPaymentDateOptions],
  )

  const disabledPromoList = useDisabledPromoCodesList(
    planPromoRules,
    creditApplication.normsCreditDecision,
    creditApplication.financingProgramId,
  )

  const merchantPaymentOptions = useMemo(
    () =>
      merchantPayments?.map((item) => (
        <MenuItem
          key={item.value}
          value={item.value}
          disabled={(disabledPromoList ?? new Set<string>()).has(item.label.split(' ')[0])}
        >
          {item.label.includes(EPaymentPlan.RegularDailyInterests)
            ? t(`EPaymentPlan.${item.label as EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : item.label}
        </MenuItem>
      )),
    [disabledPromoList, merchantPayments, t],
  )

  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const programConfig = programConfigByFinancingProgramId(creditApplication.financingProgramId)
  const availableFrequencyPayments = programConfig.frequencyPayments

  const termOptions = useMemo(
    () =>
      loanTermChoicesList.map((loanTerm) => (
        <MenuItem key={loanTerm.value} value={loanTerm.value}>
          {loanTerm.label}
        </MenuItem>
      )),
    [loanTermChoicesList],
  )

  const paymentFrequencyOptions = useMemo(
    () =>
      availableFrequencyPayments.map((frequency) => (
        <MenuItem key={frequency} value={frequency}>
          {t(`EPaymentFrequency.${frequency}`, { ns: 'enum' })}
        </MenuItem>
      )),
    [availableFrequencyPayments, t],
  )
  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledTextField
          label={t('worksheet.requestedAmount')}
          control={control}
          name="amountRequested"
          fullWidth
          disabled={disabledAmountInput}
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledStringDateOnlyPicker
          name="deliveryOn"
          control={control}
          label={t('worksheet.activationDate')}
          disablePast
          minDate={new Date()}
          maxDate={parseISO(formatDate(creditApplication.expiresOn))}
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          control={control}
          name="paymentPlanId"
          options={merchantPaymentOptions}
          label={t('worksheet.paymentPlan')}
          disableEmptyOption
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          control={control}
          name="paymentFrequency"
          label={t('worksheet.paymentFrequency')}
          options={paymentFrequencyOptions}
          disableEmptyOption
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          control={control}
          name="firstPaymentOn"
          options={firstPaymentDateOptionItems}
          label={t('worksheet.firstPaymentOn')}
          disableEmptyOption
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          control={control}
          name="term"
          label={t('worksheet.loanTerm')}
          options={termOptions}
          disabled={isLoadingTerm || hasVariableInterest}
          disableEmptyOption
          fullWidth
        />
      </Grid>
    </Grid>
  )
}

export default WorksheetDetails
